import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { FilterValueConstants } from '~commonInfra/utils/FilterValueConstants';
import { Helpers } from '~commonInfra/utils/Helpers';
import { IdentityMigrationFailureType } from '~src/authentication/models/IdentityMigrationFailureType';
import {
  BackgroundJobStatus,
  BackgroundJobTaskType,
  BackgroundJobTimeRange
} from '~src/backgroundJob/types/BackgroundJobInfo';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { CollectionVisibility } from '~src/Collections/space/CollectionModels';
import { FlowOutputStepRunStatus } from '~src/flow/models/FlowOutputStepRunInfo';
import { IFieldValue } from '~src/httpClient/serverApi/types/IFieldValue';
import {
  getSessionManagementServiceInstance, SessionManagementService
} from '~src/sessionManagement/SessionManagementService';
import { SiteRoleFilterHelper } from '~src/siteManagement/helpers/SiteRoleFilterHelper';
import { ContentFilterKeyName } from '~src/spacesPlatform/models/ContentFilters';
import { FilterFieldNames } from '~src/spacesPlatform/models/IFilterClause';
import { SearchSuggestionsService } from '~src/spacesPlatform/services/SearchSuggestionsService';
import { isRecycleBinSpace } from '~src/spacesPlatform/state/SpaceInfo';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';
import { DateHelpers } from '~src/spacesPlatform/utils/DateHelpers';
import { updateTotalUserCountForServerUserSpace } from '~src/userManagement/sagas/ServerUserSpaceSagas';
import { SiteRolesResource } from '~src/userManagement/services/resources/SiteRoles';
import { SiteRoles } from '~src/userManagement/users/serverApi/types/ISiteRole';


export type FilterCategory = 'datasource' | 'detail' | 'data' | 'date' | 'more';
export type FilterType = 'typeahead' | 'boolean' | 'text' | 'textDropdown' | 'date' | 'select' | 'radio';

export interface IFilterDefinition<TFilterValue> {
  name: ContentFilterKeyName;
  type: FilterType;
  category: FilterCategory;
  showCounts?: boolean;
  defaultValue?: TFilterValue;
  filterChangeDispatch?: (value: any) => void; // For use for dispatching any actions after filter change, currently only supported for Dropdown Filters

  // These functions map a filter value to and from the url's search param.
  valueToParams: (value: TFilterValue) => any;
  valueFromParams: (params: any) => Promise<TFilterValue>;

  // Function to apply transormations to items in typeahead fields before rendering
  transformFieldValues?: (fieldValue: IFieldValue) => IFieldValue;
}

export interface ITypeaheadFilterDefinition extends IFilterDefinition<IFieldValue> {
  field: string;
  matchPrefixesOnly: boolean;
}

export interface ISelectFilterDefinitionOption<TFilterValue> {
  translate: string;
  value: TFilterValue;
  count?: number;
  overrideLabel?: string;
}

export interface ISelectFilterDefinition<TFilterValue> extends IFilterDefinition<TFilterValue> {
  options: ISelectFilterDefinitionOption<TFilterValue>[];
}

export interface ISelectFilterDefinitionMap<TFilterValue> extends _.Dictionary<TFilterValue> {
  '': TFilterValue; //This is the required default select option value
}

export enum PasswordStatus { Any, Embedded, NotEmbedded }
export enum DatasourceType { All = 1, Embedded = 2, Published = 3 }
export enum ExtractStatus {
  All = 'all',
  Live = 'live',
  Published = 'published',  // Currently only the workbook filter supports this option
  AllExtracts = 'allExtracts',
  UnencryptedExtracts = 'unencryptedExtracts',
  EncryptedExctacts = 'encryptedExtracts',
  CurrentlyEncrypting = 'currentlyEncrypting',
  CurrentlyDecrypting = 'currentlyDecrypting'
}

export namespace FilterPanelService {

  const sessionManagementService: SessionManagementService = getSessionManagementServiceInstance();

  function createFilterDefinitions(spaceInfo: SpaceInfo): IFilterDefinition<any>[] {
    const filterDefinitions = _.compact([
      // General Filters
      !sessionManagementService.isPersonalSpaceOnSiteEnabled() ? <ITypeaheadFilterDefinition>
      {
        name: 'project',
        type: 'typeahead',
        category: 'detail',
        field: FilterFieldNames.projectId,
        defaultValue: null,
        valueFromParams: fromSingleParam('project', (paramValue: string) => SearchSuggestionsService.project(paramValue)),
        valueToParams: toSingleParam('project', (fieldValue: IFieldValue) => fieldValue.value),
        matchPrefixesOnly: false
      } : null,

      sessionManagementService.isPersonalSpaceOnSiteEnabled() ? <ITypeaheadFilterDefinition>
      {
        name: 'location',
        type: 'typeahead',
        category: 'detail',
        field: FilterFieldNames.projectId,
        defaultValue: null,
        valueFromParams: fromSingleParam('location', (paramValue: string) => SearchSuggestionsService.project(paramValue)),
        transformFieldValues: (fv: IFieldValue) => {
          if (fv.value === 'PersonalSpace') {
            return {
              ...fv,
              label: Messages.personalSpaceTitle(),
            };
          }
          return fv;
        },
        valueToParams: toSingleParam('location', (fieldValue: IFieldValue) => fieldValue.value),
        matchPrefixesOnly: false
      } : null,

      !sessionManagementService.isLimitedUserVisibilityEnforced() ? getContentOwnerFilter() : null,
      isRecycleBinSpace(spaceInfo) && FeatureFlags.recycleBinMetadataEnabled && !sessionManagementService.isLimitedUserVisibilityEnforced() ? getDeleterFilter() : null,
      isRecycleBinSpace(spaceInfo) && FeatureFlags.recycleBinMetadataEnabled ? getPathFilter() : null,

      <ITypeaheadFilterDefinition>
      {
        name: 'tag',
        type: 'typeahead',
        category: 'detail',
        field: FilterFieldNames.tags,
        defaultValue: null,
        valueFromParams: fromSingleParam('tag', (paramValue: string) => Promise.resolve({ value: paramValue, label: paramValue, count: 0 })),
        valueToParams: toSingleParam('tag', (fieldValue: IFieldValue) => fieldValue.value),
        matchPrefixesOnly: false
      },

      siteRoleFilterDefinition(),
      groupsMinimumSiteRoleDefinition(),
      maxSiteRoleDefinition(),


      afterDateFilterDefinition('createdAfter', 'date'),
      beforeDateFilterDefinition('createdBefore', 'date'),

      afterDateFilterDefinition('createdTimeAfter', 'date'),
      beforeDateFilterDefinition('createdTimeBefore', 'date'),

      afterDateFilterDefinition('modifiedAfter', 'date'),
      beforeDateFilterDefinition('modifiedBefore', 'date'),

      afterDateFilterDefinition('deletedAfter', 'date'),
      beforeDateFilterDefinition('deletedBefore', 'date'),

      afterDateFilterDefinition('updatedTimeAfter', 'date'),
      beforeDateFilterDefinition('updatedTimeBefore', 'date'),

      afterDateFilterDefinition('startedAfter', 'date'),
      beforeDateFilterDefinition('startedBefore', 'date'),
      afterDateFilterDefinition('completedAfter', 'date'),
      beforeDateFilterDefinition('completedBefore', 'date'),

      booleanFilterDefinition('favorite', 'more'),
      booleanFilterDefinition('recent', 'more'),

      // W-17228382 - The "Has an alert" filter has not been working properly since the
      // migration from Solr to CRASS. We are temporarily removing the filter from the UI
      // until the issue with the backend is fixed. This should be resolved in W-15715810
      // booleanFilterDefinition('hasAlert', 'more'),

      booleanFilterDefinition('hideEmptyCollections', 'more'),
      sessionManagementService.isSessionSiteOrServerAdmin() && booleanFilterDefinition('usersPersonalSpaceContent', 'more'),
      sessionManagementService.isOnlineEnvironment &&
      booleanFilterDefinition('isTabID', 'more'),

      <ITypeaheadFilterDefinition>
      {
        name: 'flowOutputStep',
        type: 'typeahead',
        category: 'detail',
        field: FilterFieldNames.flowOutputStepName,
        valueFromParams: fromSingleParam('flowOutputStep', (paramValue: string) => Promise.resolve({ value: paramValue, label: paramValue, count: 0 })),
        valueToParams: toSingleParam('flowOutputStep', (fieldValue: IFieldValue) => fieldValue.value),
        matchPrefixesOnly: false
      },

      dropdownSelectFilterDefinition('status',
        {
          '': 'all',
          'PENDING': FlowOutputStepRunStatus.pending,
          'INPROGRESS': FlowOutputStepRunStatus.inProgress,
          'SUCCESS': FlowOutputStepRunStatus.success,
          'FAILED': FlowOutputStepRunStatus.failed,
          // TODO tfs844917    'CANCELLED': FlowOutputStepRunStatus.cancelled,
        },
        [
          { value: 'all', translate: 'flowRunStatusAll' },
          { value: FlowOutputStepRunStatus.success, translate: 'flowRunStatusSucceeded' },
          { value: FlowOutputStepRunStatus.failed, translate: 'flowRunStatusFailed' },
          // TODO tfs844917    { value: FlowOutputStepRunStatus.cancelled, translate: 'flowRunStatusCancelled'},
          // TODO tfs844917    { value: FlowOutputStepRunStatus.cancelled, translate: 'flowRunStatusCancelled'},
          // TODO tfs844917    { value: FlowOutputStepRunStatus.cancelled, translate: 'flowRunStatusCancelled'},
          { value: FlowOutputStepRunStatus.inProgress, translate: 'flowRunStatusInProgress' },
          { value: FlowOutputStepRunStatus.pending, translate: 'flowRunStatusPending' },
        ],
        'detail'
      ),

      dropdownSelectFilterDefinition('backgroundJobStatus',
        {
          '': 'all',
          'Pending': BackgroundJobStatus.Pending,
          'InProgress': BackgroundJobStatus.InProgress,
          'Completed': BackgroundJobStatus.Completed,
          'Failed': BackgroundJobStatus.Failed,
          'Cancelled': BackgroundJobStatus.Cancelled,
        },
        [
          { value: 'all', translate: 'backgroundJobStatusAll' },
          { value: BackgroundJobStatus.Pending, translate: 'backgroundJobStatusPending' },
          { value: BackgroundJobStatus.InProgress, translate: 'backgroundJobStatusInProgress' },
          { value: BackgroundJobStatus.Completed, translate: 'backgroundJobStatusCompleted' },
          { value: BackgroundJobStatus.Failed, translate: 'backgroundJobStatusFailed' },
          { value: BackgroundJobStatus.Cancelled, translate: 'backgroundJobStatusCancelled' },
        ],
        'detail'
      ),

      backgroundJobTaskTypeDefinition(),

      dropdownSelectFilterDefinition('backgroundJobTimeRange',
        {
          '': BackgroundJobTimeRange.TwentyFour,
          '1': BackgroundJobTimeRange.One,
          '4': BackgroundJobTimeRange.Four,
          '8': BackgroundJobTimeRange.Eight,
          '12': BackgroundJobTimeRange.Twelve,
          '16': BackgroundJobTimeRange.Sixteen,
          '20': BackgroundJobTimeRange.Twenty,
        },
        [
          { value: BackgroundJobTimeRange.One, translate: 'backgroundJobTimeRange1h' },
          { value: BackgroundJobTimeRange.Four, translate: 'backgroundJobTimeRange4h' },
          { value: BackgroundJobTimeRange.Eight, translate: 'backgroundJobTimeRange8h' },
          { value: BackgroundJobTimeRange.Twelve, translate: 'backgroundJobTimeRange12h' },
          { value: BackgroundJobTimeRange.Sixteen, translate: 'backgroundJobTimeRange16h' },
          { value: BackgroundJobTimeRange.Twenty, translate: 'backgroundJobTimeRange20h' },
          { value: BackgroundJobTimeRange.TwentyFour, translate: 'backgroundJobTimeRange24h' },
        ],
        'detail'
      ),


      booleanFilterDefinition('showSystemJobs', 'detail'),

      dropdownSelectFilterDefinition('collectionVisibility',
        {
          '': 'all',
          'public': CollectionVisibility.public,
          'private': CollectionVisibility.private,
        },
        [
          { value: 'all', translate: 'collectionVisibilityAllFilter' },
          { value: CollectionVisibility.public, translate: 'collectionVisibilityPublicFilter' },
          { value: CollectionVisibility.private, translate: 'collectionVisibilityPrivate' }
        ],
        'detail'
      ),

      // Data Source Filters

      radioSelectFilterDefinition('showEmbedded',
        { // param key values mapped to value states
          '': DatasourceType.Published,
          'true': DatasourceType.All,
          'only': DatasourceType.Embedded
        },
        [ // Options
          { value: DatasourceType.All, translate: 'datasourceTypeAll' },
          { value: DatasourceType.Published, translate: 'datasourceTypePublishedOnly' },
          { value: DatasourceType.Embedded, translate: 'datasourceTypeEmbeddedOnly' }
        ],
        'datasource'
      ),

      dropdownSelectFilterDefinition('hasActiveWarning',
        {
          '': 'all',
          'false': 'false',
          'true': 'true',
        },
        [
          { value: 'all', translate: 'hasActiveWarningAll' },
          { value: 'false', translate: 'hasActiveWarningFalse' },
          { value: 'true', translate: 'hasActiveWarningTrue' },
        ],
        'datasource' // Only used for External Assets (Data Catalog)
      ),

      radioSelectFilterDefinition('hasActiveDataQualityWarning', // DQW Filter with crass search enabled
        {
          '': 'all',
          'false': 'false',
          'true': 'true',
        },
        [
          { value: 'all', translate: 'hasActiveWarningAll' },
          { value: 'false', translate: 'hasActiveWarningFalse' },
          { value: 'true', translate: 'hasActiveWarningTrue' },
        ],
        'data'
      ),
      radioSelectFilterDefinition('isEmbedded', // DQW Filter with crass search enabled
        {
          '': 'all',
          'false': 'false',
          'true': 'true',
        },
        [
          { value: 'all', translate: 'isEmbeddedAll' },
          { value: 'false', translate: 'isEmbeddedFalse' },
          { value: 'true', translate: 'isEmbeddedTrue' },
        ],
        'data'
      ),

      <ITypeaheadFilterDefinition>
      {
        name: 'connectionType',
        type: 'typeahead',
        category: 'datasource',
        field: FilterFieldNames.connectionType,
        valueFromParams: fromSingleParam('connectionType', (paramValue: string) => SearchSuggestionsService.dataConnectionType(paramValue)),
        valueToParams: toSingleParam('connectionType', (fieldValue: IFieldValue) => fieldValue.value),
        matchPrefixesOnly: true
      },

      textFilterDefinition('serverName', 'datasource'),

      radioSelectFilterDefinition('serverPort',
        {
          '': FilterValueConstants.ServerPortAny,
          'default': FilterValueConstants.ServerPortDefault
        },
        [
          { value: FilterValueConstants.ServerPortAny, translate: 'serverPortAny' },
          { value: FilterValueConstants.ServerPortDefault, translate: 'serverPortDefault' }
        ],
        'datasource'
      ),

      textFilterDefinition('databaseUsername', 'datasource'),

      radioSelectFilterDefinition('hasPassword',
        { // param key values mapped to value states
          '': PasswordStatus.Any,
          'true': PasswordStatus.Embedded,
          'false': PasswordStatus.NotEmbedded
        },
        [ // Options
          { value: PasswordStatus.Any, translate: 'hasPasswordAny' },
          { value: PasswordStatus.Embedded, translate: 'hasPasswordEmbedded' },
          { value: PasswordStatus.NotEmbedded, translate: 'hasPasswordNotEmbedded' }
        ],
        'datasource'
      ),

      !sessionManagementService.isExtractEncryptionEnabledOmniSwitch() && booleanFilterDefinition('hasExtracts', 'datasource'),
      sessionManagementService.isExtractEncryptionEnabledOmniSwitch() && dropdownSelectFilterDefinition('extractStatus',
        {
          '': ExtractStatus.All,
          'live': ExtractStatus.Live,
          // 'published' intentionally missing, the server-side filter translator currently does not support it
          'allExtracts': ExtractStatus.AllExtracts,
          'unencryptedExtracts': ExtractStatus.UnencryptedExtracts,
          'encryptedExtracts': ExtractStatus.EncryptedExctacts,
          'currentlyEncrypting': ExtractStatus.CurrentlyEncrypting,
          'currentlyDecrypting': ExtractStatus.CurrentlyDecrypting
        },
        [
          // Options
          { value: ExtractStatus.All, translate: 'extractStatusAll' },
          { value: ExtractStatus.Live, translate: 'extractStatusLive' },
          { value: ExtractStatus.AllExtracts, translate: 'extractStatusExtracts' },
          { value: ExtractStatus.UnencryptedExtracts, translate: 'extractStatusUnencrypted' },
          { value: ExtractStatus.EncryptedExctacts, translate: 'extractStatusEncrypted' },
          { value: ExtractStatus.CurrentlyEncrypting, translate: 'extractStatusEncrypting' },
          { value: ExtractStatus.CurrentlyDecrypting, translate: 'extractStatusDecrypting' }
        ],
        'datasource'
      ),
      sessionManagementService.isExtractEncryptionEnabledOmniSwitch() && dropdownSelectFilterDefinition('workbookConnFilter',
        {
          '': ExtractStatus.All,
          'live': ExtractStatus.Live,
          'published': ExtractStatus.Published,
          'allExtracts': ExtractStatus.AllExtracts,
          'unencryptedExtracts': ExtractStatus.UnencryptedExtracts,
          'encryptedExtracts': ExtractStatus.EncryptedExctacts,
          'currentlyEncrypting': ExtractStatus.CurrentlyEncrypting,
          'currentlyDecrypting': ExtractStatus.CurrentlyDecrypting
        },
        [
          // Options
          { value: ExtractStatus.All, translate: 'extractStatusAll' },
          { value: ExtractStatus.Live, translate: 'extractStatusLive' },
          { value: ExtractStatus.AllExtracts, translate: 'extractStatusExtracts' },
          { value: ExtractStatus.Published, translate: 'workbookConnFilterPublished' },
          { value: ExtractStatus.UnencryptedExtracts, translate: 'extractStatusUnencrypted' },
          { value: ExtractStatus.EncryptedExctacts, translate: 'extractStatusEncrypted' },
          { value: ExtractStatus.CurrentlyEncrypting, translate: 'extractStatusEncrypting' },
          { value: ExtractStatus.CurrentlyDecrypting, translate: 'extractStatusDecrypting' }
        ],
        'datasource'
      ),
      sessionManagementService.isExtractEncryptionEnabledOmniSwitch() && booleanFilterDefinition('includeLiveToDataEngine', 'datasource'),
      booleanFilterDefinition('isCertified', 'datasource'),

      FeatureFlags.identityMigrationEnabled && booleanFilterDefinition('discardIdentity', 'more'),

      FeatureFlags.identityMigrationEnabled && dropdownSelectFilterDefinition('failureType',
        {
          '': 'all',
          'AMBIGUOUS': IdentityMigrationFailureType.AMBIGUOUS,
          'DUPLICATE': IdentityMigrationFailureType.DUPLICATE,
          'NON_EXISTENT': IdentityMigrationFailureType.NON_EXISTENT,
          'NON_LOCAL_DOMAIN': IdentityMigrationFailureType.NON_LOCAL_DOMAIN,
          'UNKNOWN': IdentityMigrationFailureType.UNKNOWN,
        },
        [
          { value: 'all', translate: 'identityMigration_FailureTypeGroup_AllFailed' },
          { value: IdentityMigrationFailureType.AMBIGUOUS, translate: 'identityMigration_FailureTypeGroup_Ambiguous' },
          { value: IdentityMigrationFailureType.DUPLICATE, translate: 'identityMigration_FailureTypeGroup_Duplicate' },
          { value: IdentityMigrationFailureType.NON_EXISTENT, translate: 'identityMigration_FailureTypeGroup_NonExistent' },
          { value: IdentityMigrationFailureType.NON_LOCAL_DOMAIN, translate: 'identityMigration_FailureTypeGroup_NonLocal' },
          { value: IdentityMigrationFailureType.UNKNOWN, translate: 'identityMigration_FailureTypeGroup_Unknown' },
        ],
        'detail'
      ),

      FeatureFlags.identityPoolsEnabled && dropdownSelectFilterDefinition(
        'identityPool',
        {'': ''},
        []
      ),

    ]) as IFilterDefinition<any>[];

    return filterDefinitions;
  }


  function siteRoleFilterDefinition() {
    const allSiteRolesExceptGuest = _.without(SiteRolesResource.instance.allOptions(), SiteRoles.Guest);
    return siteRoleSelectFilterDefinition('siteRole', allSiteRolesExceptGuest, false);
  }

  function groupsMinimumSiteRoleDefinition() {
    return siteRoleSelectFilterDefinition('groupsMinimumSiteRole', SiteRolesResource.instance.optionsForCurrentUserExcept(SiteRoles.ServerAdministrator), false, false);
  }

  function backgroundJobTaskTypeDefinition() {
    let paramToValueMap = {
      '': 'all',
      'Bridge': BackgroundJobTaskType.Bridge,
      'Extract': BackgroundJobTaskType.Extract,
      'Flow': BackgroundJobTaskType.Flow,
      'Subscription': BackgroundJobTaskType.Subscription,
      'System': BackgroundJobTaskType.System,
      'Encryption': BackgroundJobTaskType.Encryption,
    };
    let options = [
      { value: 'all', translate: 'backgroundJobTaskTypeAll' },
      { value: BackgroundJobTaskType.Bridge, translate: 'backgroundJobTaskTypeBridge' },
      { value: BackgroundJobTaskType.Extract, translate: 'backgroundJobTaskTypeExtract' },
      { value: BackgroundJobTaskType.Flow, translate: 'backgroundJobTaskTypeFlow' },
      { value: BackgroundJobTaskType.Subscription, translate: 'backgroundJobTaskTypeSubscription' },
      { value: BackgroundJobTaskType.System, translate: 'backgroundJobTaskTypeSystem' },
      { value: BackgroundJobTaskType.Encryption, translate: 'backgroundJobTaskTypeEncryption' },
    ];

    // Non server admins cannot see the System  jobs option
    if (!sessionManagementService.isSessionServerAdmin()) {
      delete paramToValueMap.System;
      options = options.filter(o => o.value !== BackgroundJobTaskType.System);
    }
    return dropdownSelectFilterDefinition('backgroundJobTaskType', paramToValueMap, options, 'detail');
  }

  function maxSiteRoleDefinition() {
    return siteRoleSelectFilterDefinition('maxSiteRole', SiteRolesResource.instance.optionsForCurrentUser(), true);
  }

  function siteRoleSelectFilterDefinition(name: ContentFilterKeyName, roles: string[], isServerUserSpaceFilter: boolean, showCounts = true) {
    return dropdownSelectFilterDefinition(name,

      // param key values mapped to value states
      // Map '' to "any role"
      _.extend({}, SiteRoles, { '': SiteRoleFilterHelper.AnyRole }),

      SiteRoleFilterHelper.siteRoleOptions(roles),
      'detail',
      showCounts,
      (value: any) => {isServerUserSpaceFilter && getAppStore().dispatch(updateTotalUserCountForServerUserSpace(value));}
    );
  }

  export function dropdownSelectFilterDefinition<TFilterValue>(
    name: ContentFilterKeyName,
    paramToValueMap: ISelectFilterDefinitionMap<TFilterValue>,
    options: ISelectFilterDefinitionOption<TFilterValue>[],
    category?: FilterCategory,
    showCounts?: boolean,
    filterChangeDispatch?: (value: any) => void
  ): ISelectFilterDefinition<TFilterValue> {
    const valueToParamMap = Helpers.swapDict(paramToValueMap);
    return {
      name: name,
      type: 'select',
      category: category,
      defaultValue: paramToValueMap[''],
      valueFromParams: (params: any) => Promise.resolve(paramToValueMap[params[name]] || paramToValueMap['']), //Bypass fromSingleParam to handle the undefined param case
      valueToParams: toSingleParam(name, (val: TFilterValue) => (valueToParamMap[val + ''] || null)),
      options: options,
      showCounts,
      filterChangeDispatch
    };
  }

  function radioSelectFilterDefinition<TFilterValue>(
    name: ContentFilterKeyName,
    paramToValueMap: ISelectFilterDefinitionMap<TFilterValue>,
    options: ISelectFilterDefinitionOption<TFilterValue>[],
    category?: FilterCategory,
  ): ISelectFilterDefinition<TFilterValue> {
    const valueToParamMap = Helpers.swapDict(paramToValueMap);
    return {
      name: name,
      type: 'radio',
      category: category,
      defaultValue: paramToValueMap[''],
      valueFromParams: (params: any) => Promise.resolve(paramToValueMap[params[name]] || paramToValueMap['']), //Bypass fromSingleParam to handle the undefined param case
      valueToParams: toSingleParam(name, (val: TFilterValue) => (valueToParamMap[val + ''] || null)),
      options: options
    };
  }

  function booleanFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<boolean> {
    return {
      name: name,
      type: 'boolean',
      category: category,
      valueFromParams: fromSingleParam(name, (paramValue: string) => Promise.resolve(Helpers.toBoolean(paramValue))),
      valueToParams: toSingleParam(name, (value: boolean) => '' + value)
    };
  }

  function getContentOwnerFilter(): ITypeaheadFilterDefinition {
    return {
      name: 'owner',
      type: 'typeahead',
      category: 'detail',
      field: FilterFieldNames.ownerId,
      valueFromParams: fromSingleParam('owner', (paramValue: string) => SearchSuggestionsService.user(paramValue)),
      valueToParams: toSingleParam('owner', (fieldValue: IFieldValue) => fieldValue.value),
      matchPrefixesOnly: true
    };
  }

  function getDeleterFilter(): ITypeaheadFilterDefinition {
    return {
      name: 'deleter',
      type: 'typeahead',
      category: 'detail',
      field: FilterFieldNames.deleterId,
      valueFromParams: fromSingleParam('deleter', (paramValue: string) => SearchSuggestionsService.user(paramValue)),
      valueToParams: toSingleParam('deleter', (fieldValue: IFieldValue) => fieldValue.value),
      matchPrefixesOnly: true
    };
  }

  function getPathFilter(): ITypeaheadFilterDefinition {
    return {
      name: 'path',
      type: 'typeahead',
      category: 'detail',
      field: FilterFieldNames.path,
      valueFromParams: fromSingleParam('path', (paramValue: string) => Promise.resolve({ value: paramValue, label: paramValue, count: 0 })),
      valueToParams: toSingleParam('path', (fieldValue: IFieldValue) => fieldValue.label),
      matchPrefixesOnly: true
    };
  }


  function textFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<string> {
    return {
      name: name,
      type: 'text',
      category: category,
      valueFromParams: fromSingleParam(name, (paramValue: string) => Promise.resolve(paramValue)),
      valueToParams: toSingleParam(name, (text: string) => text)
    };
  }

  function afterDateFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<Date> {
    return {
      name: name,
      type: 'date',
      category: category,
      defaultValue: undefined,
      valueFromParams: fromSingleParam(name, (paramValue: string) => dateFromISOString(paramValue)),
      valueToParams: toSingleParam(name, (date: Date) => DateHelpers.toISOString(date))
    };
  }

  function beforeDateFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<Date> {
    return {
      name: name,
      type: 'date',
      category: category,
      defaultValue: undefined,
      valueFromParams: fromSingleParam(name, (paramValue: string) => dateFromISOString(paramValue)),
      valueToParams: toSingleParam(name, (date: Date) => DateHelpers.toISOString(DateHelpers.endOfDay(date)))
    };
  }

  function dateFromISOString(paramValue: string): Promise<Date> {
    return Promise.resolve(DateHelpers.midnight(DateHelpers.fromISOString(paramValue)));
  }

  function fromSingleParam<TFilterValue>(paramName: ContentFilterKeyName, valueFn: (paramValue: string) => Promise<TFilterValue>) {
    return (params: any) => params[paramName] ? valueFn(params[paramName]) : Promise.resolve(null);
  }

  function toSingleParam<TFilterValue>(paramName: ContentFilterKeyName, paramValueFn: (value: TFilterValue) => string) {
    return (value: TFilterValue) => {
      const params: any = {};
      params[paramName] = value ? paramValueFn(value) : null;
      return params;
    };
  }

  export function getFilterDefinitions(filterNames: ContentFilterKeyName[], spaceInfo: SpaceInfo): IFilterDefinition<any>[] {
    const filterDefinitions = createFilterDefinitions(spaceInfo);
    const filterDefinitionsMap = _.indexBy(filterDefinitions, 'name');
    const defns = _.map(filterNames, (filterName: ContentFilterKeyName) => filterDefinitionsMap[filterName]);
    return _.filter(defns, (defn: IFilterDefinition<any>) => defn != null);
  }

}

export type FilterPanelService = typeof FilterPanelService;
